import { useState, useEffect } from 'react';
import { Trophy, X, Medal, Crown } from 'lucide-react';
import { getMyUniverse, getLeaderboard } from '../../api/universe';
import type { LeaderboardEntry } from '../../api/universe';

interface LeaderboardPanelProps {
  onClose: () => void;
}

const rankIcons = [
  <Crown className="w-5 h-5 text-yellow-400" />,
  <Medal className="w-5 h-5 text-slate-300" />,
  <Medal className="w-5 h-5 text-amber-600" />,
];

const stageNames = [
  'Barren', 'Terraforming', 'Spark of Life', 'Plant Life',
  'Complex Life', 'Civilisation', 'Technology', 'Space Technology', 'Space Exploration',
];

const scoreLabels: Record<string, string> = {
  development: 'Dev',
  exploration: 'Explore',
  trade: 'Trade',
  peace: 'Peace',
  defence: 'Defence',
  research: 'Research',
  dominion: 'Dominion',
  doctrineBonus: 'Doctrine',
};

function topBreakdown(entry: LeaderboardEntry) {
  return Object.entries(entry.scoreBreakdown || {})
    .filter(([key, value]) => key !== 'eventCount' && value > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2);
}

export default function LeaderboardPanel({ onClose }: LeaderboardPanelProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const universeRes = await getMyUniverse();
        if (!universeRes.success || !universeRes.data.leaderboard_enabled) {
          setError('Leaderboard is not enabled for your class.');
          setLoading(false);
          return;
        }
        const lbRes = await getLeaderboard(universeRes.data.universe_id);
        if (lbRes.success) {
          setEntries(lbRes.data);
        } else {
          setError('Could not load leaderboard.');
        }
      } catch (e) {
        setError('Leaderboard unavailable.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-bold">Class Leaderboard</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : error ? (
            <div className="text-center py-8 text-slate-400">{error}</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No classmates yet.</div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, idx) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${
                    idx < 3 ? 'bg-slate-800/60' : 'bg-slate-900/40'
                  }`}
                >
                  <div className="w-8 flex justify-center">
                    {idx < 3 ? rankIcons[idx] : (
                      <span className="text-sm font-mono text-slate-500">{idx + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{entry.name}</div>
                    <div className="text-xs text-slate-400 truncate">{entry.planet_name}</div>
                    {topBreakdown(entry).length > 0 && (
                      <div className="mt-1 flex gap-1 overflow-hidden">
                        {topBreakdown(entry).map(([key, value]) => (
                          <span key={key} className="rounded bg-slate-950/70 px-1.5 py-0.5 text-[10px] text-slate-300">
                            {scoreLabels[key] || key}: {Math.round(value)}
                          </span>
                        ))}
                      </div>
                    )}
                    {entry.recentScoreEvents?.[0] && (
                      <div className="mt-1 truncate text-[10px] text-green-300">
                        +{Math.round(entry.recentScoreEvents[0].points)} {entry.recentScoreEvents[0].reason}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono font-semibold text-yellow-400">
                      {entry.score || 0}
                    </div>
                    <div className="text-xs text-slate-500">{entry.factionGoal || stageNames[entry.stage]}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
